// Names and orders the pipeline's steps for the people watching them run.
//
// The ingest emits a fixed, ordered set of steps (see lineage.run_step:
// land:company → land:person → land:job → land:person_employment → land:activity
// → land:user → map:company → map:people → map:jobs → map:person_employments →
// map:activities → derive:company_website → derive:company_country →
// derive:people_country → derive:people_classify → journey:candidates →
// quality:profile), which is what lets the import show a true "step 4 of 17"
// and a real percentage rather than an invented bar. Enrichment's step count
// varies with the recipe, so it has no manifest and renders as a growing list
// instead.

export const INGEST_STEPS = [
  'land:company',
  'land:person',
  'land:job',
  // person_employment/activity are landed after the entities they reference;
  // user is LAND-ONLY (no map:user — recruiters aren't mapped onto a canonical
  // record today), so it has no counterpart in the map phase below.
  'land:person_employment',
  'land:activity',
  'land:user',
  'map:company',
  'map:people',
  'map:jobs',
  'map:person_employments',
  'map:activities',
  // The derive stage: free, deterministic fills over the data the import just
  // landed (emails→website, ccTLD→countries, titles→function/seniority).
  // D1 runs first because company/people country read the websites it fills.
  'derive:company_website',
  'derive:company_country',
  'derive:people_country',
  'derive:people_classify',
  // The candidate journey runs after the map phase because each of its events
  // points at a person, a job, a stage and an actor — all four have to exist
  // canonically first. A source that carries no recruitment pipeline still
  // reports this step, at zero rows, so the manifest holds for every import.
  'journey:candidates',
  // The run profiles the data it just produced and stores the result, so the Data
  // page reads a row instead of re-scanning the raw records on every visit.
  'quality:profile'
];

// `land:*` writes the raw ATS records; `map:*` maps them onto the canonical
// records. Said plainly: we read them, then we clean them.
const PHASE = { land: 'Reading', map: 'Cleaning', derive: 'Deriving', enrich: 'Enriching' };
const ENTITY = {
  company: 'companies',
  person: 'people',
  people: 'people',
  job: 'jobs',
  jobs: 'jobs',
  person_employment: 'employment history',
  person_employments: 'employment history',
  activity: 'activities',
  activities: 'activities',
  // Recruiters, in ATS terms, are "users" — land-only, so this reads only as
  // "Reading recruiters" (no "Cleaning recruiters" step exists).
  user: 'recruiters'
};

// Steps whose name doesn't decompose into verb + subject. The journey both reads
// and cleans in one stage, so neither verb alone describes it — it is named for
// what it produces: which candidates were put forward for which jobs.
const NAMED = {
  'quality:profile': 'Checking quality',
  'journey:candidates': 'Linking candidates to jobs',
  // The only derive step that doesn't decompose into verb + subject: it
  // classifies people by title (function/seniority), not a field named
  // "classify".
  'derive:people_classify': 'Classifying people from titles'
};

export function stepLabel(name) {
  if (NAMED[name]) return NAMED[name];
  const [phase, rest] = String(name || '').split(':');
  const verb = PHASE[phase];
  if (!verb || !rest) return name;
  const subject = ENTITY[rest] || rest.replace(/_/g, ' ');
  return `${verb} ${subject}`;
}

// Fraction of the whole run: every finished step, plus how far the running one
// has got. Returns null when the run's steps don't match the manifest — better
// to show no percentage than a confidently wrong one.
// The enrichment recipe's USER-FACING steps, in dependency order, labelled in one
// "Entity - Field" standard so every surface reads the same (the confirm-dialog
// checklist AND the live job panel). Single source of truth: change a label here and
// it changes everywhere. 'global' (the registry match) is SignalCue-internal — it runs
// as part of the recipe but is deliberately absent here, so it never shows to a user.
export const ENRICH_STEPS = [
  { name: 'company_country', label: 'Company - Country' },
  { name: 'website', label: 'Company - Website' },
  { name: 'company_linkedin', label: 'Company - LinkedIn' },
  { name: 'company_industry', label: 'Company - Industry' },
  { name: 'company_city', label: 'Company - City' },
  { name: 'people_location', label: 'People - Location' },
  { name: 'people_linkedin', label: 'People - LinkedIn' }
];

const ENRICH_LABEL = Object.fromEntries(ENRICH_STEPS.map((s) => [s.name, s.label]));

// enrichStepLabel maps a recipe step name to its display label; an unknown name (e.g.
// the internal 'global') falls back to the raw name — but such steps are filtered from
// the UI upstream, so it should never surface.
export const enrichStepLabel = (name) => ENRICH_LABEL[name] || name;

export function overallProgress(state, manifest) {
  if (!manifest || !manifest.length || !state.steps.length) return null;
  if (state.steps.some((s) => !manifest.includes(s.name))) return null;

  const completed = state.steps.filter((s) => s.status === 'success').length;
  const running = state.steps.find((s) => s.status === 'running');
  const partial = running && running.total ? Math.min(1, (running.done || 0) / running.total) : 0;

  return {
    stepCount: manifest.length,
    // Between two steps nothing is running, so point at the one about to start
    // rather than lingering on the one that just finished.
    stepIndex: completed >= manifest.length ? manifest.length : completed + 1,
    fraction: Math.min(1, (completed + partial) / manifest.length)
  };
}
